import { useEffect, useState } from 'react';

type ResolvedTheme = 'light' | 'dark';

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

/**
 * Reads the theme currently applied to the document, mirroring the CSS variable
 * rules in globals.css:
 *   - `.light` class present -> light
 *   - `.dark` class present  -> dark
 *   - neither (`system`)     -> follows the OS `prefers-color-scheme`
 *
 * `_app` keeps the root class in sync with the user's preference (including the
 * native/Capacitor-backed store), so deriving from the DOM here means themed
 * images share the exact same source of truth as the rest of the UI without
 * re-reading the preference (and without pulling Capacitor into every icon).
 */
function readResolvedTheme(): ResolvedTheme {
  if (typeof document === 'undefined') return 'light';
  const root = document.documentElement;
  if (root.classList.contains('light')) return 'light';
  if (root.classList.contains('dark')) return 'dark';
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia(DARK_MEDIA_QUERY).matches ? 'dark' : 'light';
  }
  return 'light';
}

/**
 * Resolves the active theme to a concrete `'light' | 'dark'`. Reactive to both
 * the root class changing (when the user switches theme, `_app` toggles it) and
 * the OS `prefers-color-scheme` flipping while the preference is `system`. This
 * is the single source of truth `useThemedImageSource` uses to pick a variant.
 */
export function useResolvedTheme(): ResolvedTheme {
  // SSR/first paint: default to light so server and client markup agree; the
  // effect corrects it on mount before paint.
  const [theme, setTheme] = useState<ResolvedTheme>('light');

  useEffect(() => {
    const update = () => setTheme(readResolvedTheme());
    update();

    // React to `.light`/`.dark` being toggled on the root by the theme system.
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // React to the OS preference (only consulted when no class is set).
    const mql = window.matchMedia?.(DARK_MEDIA_QUERY);
    mql?.addEventListener('change', update);

    return () => {
      observer.disconnect();
      mql?.removeEventListener('change', update);
    };
  }, []);

  return theme;
}
